import React, { useRef, useState } from 'react'
import { Column } from 'primereact/column'
import { useSelector } from 'react-redux'
import { Button } from 'primereact/button'
import { PrimeIcons } from 'primereact/api'
import { Menu } from 'primereact/menu'

import { Table } from '../../uikit/Table'
import { NfcTag, useNfcTagDeleteMutation } from '../../redux/api/web-api'
import { RfidTagDialog } from '../../dialogs/rfid-tag-dialog'
import convertHexToBigInt from '../../../lib/convertHexToBigInt'
import { RootState } from '../../../admin/redux/store'
import { useRFIDTagsQuery } from '../../hooks/useFetchEndpoints'
import { isAdminDomain } from '../../../lib/currentDomain'
import { Input } from '../../uikit/Input'
import useText from '../../hooks/useText'
import { useToast } from '../../uikit/ToastProvider'
import { confirm } from '../../uikit/ConfirmDialog'

import { RfidTagPoolsDialog } from './dialogs/rfid-tag-pools-dialog'
import { RfidTagChargePointsDialog } from './dialogs/rfid-tag-charge-points-dialog'

export const RFIDTagsWidget = () => {
    const text = useText()
    const { partnerId, selectedCompanyId } = useSelector((state: RootState) => state.user)

    const { data: rfidTags = [], isLoading: isLoadingRfidTags } = useRFIDTagsQuery(partnerId, selectedCompanyId)
    const [deleteTag] = useNfcTagDeleteMutation()

    const [globalFilter, setGlobalFilter] = useState('')

    const menuRef = useRef<Menu>(null)
    const toast = useToast()

    const [rfidTag, setRfidTag] = useState<NfcTag | undefined>(undefined)
    const [rfidTagDialogIsOpen, setRfidTagDialogIsOpen] = useState(false)
    const [rfidTagPoolsDialogIsOpen, setRfidTagPoolsDialogIsOpen] = useState(false)
    const [rfidTagChargePointsDialogIsOpen, setRfidTagChargePointsDialogIsOpen] = useState(false)

    const editAdmin = (rfidTag: NfcTag) => {
        setRfidTag(rfidTag)
        setRfidTagDialogIsOpen(true)
    }

    const hideRfidTagDialog = () => {
        setRfidTagDialogIsOpen(false)
        setRfidTag(undefined)
    }

    const handleRemoveRFIDTag = async (rfidTag: NfcTag) => {
        if (!rfidTag) return

        const confirmed = await confirm({
            header: text('lblProceedConfirmation'),
            message: text('msgDoYouWantToDeleteRFID', { id: rfidTag.id }),
            variant: 'danger',
        })
        if (!confirmed) return

        deleteTag({
            nfcTagId: rfidTag.id!,
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgRFIDDeleted'))
            })
            .catch(() => {
                toast.notifyError(text('msgRFIDNotDeleted'))
            })
    }

    return (
        <>
            <div className="flex align-items-center justify-content-end mb-4">
                <Button
                    label={text('btnCreate')}
                    icon="pi pi-plus"
                    onClick={() => setRfidTagDialogIsOpen(true)}
                    className="mr-2"
                />
                <Input
                    id="rfid-search-input"
                    type="search"
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={text('lblSearch')}
                    icon={PrimeIcons.SEARCH}
                />
            </div>

            <Menu
                model={[
                    {
                        label: text('lblRFIDAccessToPools'),
                        command: () => setRfidTagPoolsDialogIsOpen(true),
                        disabled: !rfidTag?.companyId,
                    },
                    {
                        label: text('lblRFIDAccessToChargers'),
                        command: () => setRfidTagChargePointsDialogIsOpen(true),
                        disabled: !rfidTag?.companyId,
                    },
                ]}
                ref={menuRef}
                popup
            />

            <Table
                value={rfidTags}
                globalFilter={globalFilter}
                globalFilterFields={['id', 'companyName', 'fullName', 'externalId', 'comment']}
                removableSort
                loading={isLoadingRfidTags}
                paginator
                emptyMessage={text('msgNoRfidTagsFound')}
                data-testid="rfid-tags"
            >
                <Column field="id" header={text('lblId')} sortable></Column>
                <Column
                    body={(rowData: NfcTag) => convertHexToBigInt(rowData.id!)}
                    header={text('lblRfidDex')}
                ></Column>
                <Column
                    body={(rowData: NfcTag) => convertHexToBigInt(rowData.id!, true)}
                    header={text('lblRfidReverse')}
                ></Column>
                <Column
                    field="companyName"
                    header={text('lblOrganisationNameField')}
                    sortable
                    hidden={isAdminDomain}
                ></Column>
                <Column field="fullName" header={text('lblUserOrOwner')} sortable></Column>
                <Column field="externalId" header={text('lblExternalId')} sortable></Column>
                <Column field="comment" header={text('lblComment')} sortable></Column>

                <Column
                    body={(rowData: NfcTag) => {
                        return (
                            <div className="flex gap-3">
                                <Button
                                    size="small"
                                    icon="pi pi-pencil"
                                    rounded
                                    outlined
                                    severity="info"
                                    onClick={() => editAdmin(rowData)}
                                />
                                <Button
                                    size="small"
                                    icon="pi pi-cog"
                                    rounded
                                    outlined
                                    onClick={(event) => {
                                        setRfidTag(rowData)
                                        menuRef.current?.toggle(event)
                                    }}
                                    disabled={!rowData.companyId}
                                    tooltip={rowData?.companyId ? undefined : text('lblRFIDSettingsRestrictionTooltip')}
                                    tooltipOptions={{ position: 'left', showOnDisabled: true }}
                                />
                                <Button
                                    size="small"
                                    icon="pi pi-trash"
                                    rounded
                                    outlined
                                    severity="danger"
                                    onClick={() => handleRemoveRFIDTag(rowData)}
                                />
                            </div>
                        )
                    }}
                ></Column>
            </Table>

            <RfidTagDialog rfidTag={rfidTag} onHide={hideRfidTagDialog} visible={rfidTagDialogIsOpen} />

            <RfidTagPoolsDialog
                visible={rfidTagPoolsDialogIsOpen}
                onHide={() => setRfidTagPoolsDialogIsOpen(false)}
                rfidTag={rfidTag}
            />

            <RfidTagChargePointsDialog
                visible={rfidTagChargePointsDialogIsOpen}
                onHide={() => setRfidTagChargePointsDialogIsOpen(false)}
                rfidTag={rfidTag}
            />
        </>
    )
}
